import { useState } from 'react'

import { FeatureFlags, useFeatureFlag } from '@/providers/FeatureFlagContext'
import DropdownButton from '@components/DropdownButton/DropdownButton'
import { MarketBasketStatus, MarketBasketWithSettings } from '@domain'
import { faBagShoppingMinus, faDownload, faGear, faPen } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, ButtonProps, Stack, Tooltip, useMediaQuery } from '@mui/material'
import ActionCenterButton from '@views/MarketBaskets/ActionCenter/Components/ActionCenterButton/ActionCenterButton'
import BasketSettings from '@views/MarketBaskets/BasketSettings/BasketSettings'
import BlanketMarkupButton from '@views/MarketBaskets/BlanketMarkup/BlanketMarkupButton/BlanketMarkupButton'
import { useNavigate } from 'react-router-dom'

import BasketExclusions from '../../BasketExclusions/BasketExclusions'
import ExcelExport from '../../BasketExport/ExcelExport'
import PdfExport from '../../BasketExport/PdfExport'

export type ButtonBarProps = {
	isLoading: boolean
	isAnalysisComplete: boolean
	marketBasket: MarketBasketWithSettings
}

export function ButtonBar({ isLoading, isAnalysisComplete, marketBasket }: ButtonBarProps) {
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [exclusionsOpen, setExclusionsOpen] = useState(false)
	const [excelExportOpen, setExcelExportOpen] = useState(false)
	const [pdfExportOpen, setPdfExportOpen] = useState(false)

	const largeScreen = useMediaQuery('(min-width:1950px)')
	const smallScreen = !largeScreen

	const actionCenterEnabled = useFeatureFlag(FeatureFlags.ActionCenter)
	const navigate = useNavigate()

	return (
		<>
			{/* Drawers ---------  */}
			{excelExportOpen && (
				<ExcelExport
					marketBasketId={marketBasket.id}
					open
					onClose={() => setExcelExportOpen(false)}
				/>
			)}
			{pdfExportOpen && <PdfExport open onClose={() => setPdfExportOpen(false)} />}
			{settingsOpen && <BasketSettings open onClose={() => setSettingsOpen(false)} />}
			{exclusionsOpen && <BasketExclusions open onClose={() => setExclusionsOpen(false)} />}

			{/* Buttons ---------  */}
			<Stack ml={2} direction="row" gap={1} alignItems="center" sx={{ height: '100%' }}>
				<BlanketMarkupButton
					iconOnly={smallScreen}
					loading={marketBasket?.status === MarketBasketStatus.RECALCULATING}
					disabled={isLoading}
				/>
				{actionCenterEnabled && <ActionCenterButton iconOnly={smallScreen} disabled={isLoading} />}

				<ResponsiveButton
					loading={marketBasket?.status === MarketBasketStatus.RECALCULATING}
					disabled={isLoading}
					onClick={() => setSettingsOpen(true)}
					startIcon={<FontAwesomeIcon icon={faGear} />}
					variant="outlined"
					data-testid="basket-settings"
					collapse={smallScreen}
				>
					Settings
				</ResponsiveButton>

				<ResponsiveButton
					loading={marketBasket?.status === MarketBasketStatus.RECALCULATING}
					disabled={isLoading}
					onClick={() => setExclusionsOpen(true)}
					startIcon={<FontAwesomeIcon icon={faBagShoppingMinus} />}
					variant="outlined"
					data-testid="basket-exclusions"
					collapse={smallScreen}
				>
					Exclusions
				</ResponsiveButton>

				<DropdownButton
					disabled={!isAnalysisComplete || isLoading}
					size="small"
					options={[
						{
							label: 'Export to PDF',
							onClick: () => setPdfExportOpen(true),
						},
						{
							label: 'Export to Excel',
							onClick: () => setExcelExportOpen(true),
						},
					]}
					primaryButton={
						<ResponsiveButton
							loading={isLoading}
							disabled={!isAnalysisComplete}
							onClick={() => setExcelExportOpen(true)}
							startIcon={<FontAwesomeIcon icon={faDownload} />}
							variant="outlined"
							data-testid="export-basket"
							collapse={smallScreen}
						>
							Export
						</ResponsiveButton>
					}
				/>
				<Button
					sx={{ whiteSpace: 'nowrap' }}
					disabled={isLoading}
					onClick={() => navigate(`edit`)}
					startIcon={<FontAwesomeIcon icon={faPen} size="sm" />}
					variant="contained"
					data-testid="edit-basket"
				>
					{`Edit ${largeScreen ? 'Basket' : ''}`}
				</Button>
			</Stack>
		</>
	)
}

function ResponsiveButton({
	loading,
	startIcon,
	variant = 'outlined',
	color = 'primary',
	disabled = false,
	size = 'medium',
	onClick,
	children = null,
	collapse,
	...rest
}: ButtonProps & { collapse: boolean }) {
	return (
		<Tooltip title={collapse ? children : undefined} placement="bottom">
			<span>
				<Button
					loading={loading}
					startIcon={startIcon}
					variant={variant}
					color={color}
					disabled={disabled || loading}
					onClick={onClick}
					loadingPosition="start"
					size={size}
					{...rest}
					sx={
						collapse
							? {
									minWidth: 'auto',
									padding: 1,
									'& .MuiButton-startIcon': {
										margin: 0,
									},
								}
							: undefined
					}
				>
					{!collapse && children}
				</Button>
			</span>
		</Tooltip>
	)
}
